package dev.lingvo.site.ds

import kotlinx.browser.window

/**
 * The modifier this machine's keyboard actually has.
 *
 * Every shortcut listener in the product accepts `metaKey || ctrlKey`, so the chords work on
 * Windows and Linux — but the interface only ever drew `⌘`. Somebody on Windows reading "⌘ K" is
 * being shown a key their keyboard does not have, for a shortcut that would have worked; the help
 * page's whole purpose is defeated by naming the wrong key.
 *
 * Read once at first use rather than per composition: the platform does not change while the page
 * is open, and this is called from render paths that run on every keystroke in the editor.
 */
val commandKeyLabel: String by lazy {
    val platform = runCatching {
        val nav = window.navigator.asDynamic()
        // `userAgentData.platform` where it exists, falling back to the long-deprecated but
        // universally present `navigator.platform`.
        (nav.userAgentData?.platform as? String)?.takeIf { it.isNotBlank() }
            ?: (window.navigator.platform as? String).orEmpty()
    }.getOrDefault("")
    val apple = platform.contains("mac", ignoreCase = true) ||
        platform.contains("iphone", ignoreCase = true) ||
        platform.contains("ipad", ignoreCase = true)
    if (apple) "⌘" else "Ctrl"
}

/** `⌘K` on a Mac, `Ctrl K` elsewhere — spaced so the two read as two keys rather than one token. */
fun commandChord(key: String): String = if (commandKeyLabel == "⌘") "⌘$key" else "Ctrl $key"

/**
 * The same chord as [commandChord], as the keys it is actually made of.
 *
 * [LChord] draws one `<kbd>` per key, so it needs the parts and not the sentence — `⌘` and `K` are
 * two things a reader presses, and one `<kbd>⌘K</kbd>` says they are one. Written here rather than
 * in `LChord.kt` because the platform question is this file's, and because the two spellings of the
 * command key have to stay in one place: a second `if (isMac)` is a second chance to disagree.
 */
fun commandChordKeys(key: String): List<String> = listOf(commandKeyLabel, key)

/**
 * The modifier glyphs that stand on their own in a written chord.
 *
 * `⇧` and `↓` are separate presses; `Esc` and `Enter` are single keys whose names happen to be
 * several letters long. Splitting on characters would break the second group, so only these are
 * ever split off the front of a token.
 */
private val CHORD_MODIFIER_GLYPHS = setOf('⌘', '⇧', '⌥', '⌃', '^')

/**
 * Reads a chord that was written as one string into the keys it names.
 *
 * The product spells its shortcuts three ways and every one of them is already in the source:
 * `"⌘K"` from [commandChord] on a Mac, `"Ctrl K"` from the same function everywhere else, and
 * `"⌘⇧↓"` in the editor's shortcut list. A component that draws one `<kbd>` per key has to accept
 * all three, so this splits on the separators a human would write and then peels the leading
 * modifier glyphs off whatever is left.
 *
 * Deliberately not a parser of key names: `"Ctrl K"` gives `["Ctrl", "K"]` because of the space,
 * not because `Ctrl` is recognised. Anything unrecognised survives as one key, which is the right
 * failure — a chord drawn as a single wide `<kbd>` is legible; a chord shredded into letters is not.
 */
fun chordKeys(chord: String): List<String> {
    val trimmed = chord.trim()
    if (trimmed.isEmpty()) return emptyList()
    val tokens = trimmed.split(' ', '+').map(String::trim).filter(String::isNotEmpty)
    return tokens.flatMap { token ->
        val keys = mutableListOf<String>()
        var index = 0
        while (index < token.length && token[index] in CHORD_MODIFIER_GLYPHS) {
            keys += token[index].toString()
            index++
        }
        val remainder = token.substring(index)
        if (remainder.isNotEmpty()) keys += remainder
        keys
    }
}
